/**
 * Alerts Portal - FINAL PRODUCTION VERSION
 * With Working Filters
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  BarChart3,
  Filter,
  ChevronLeft,
  TriangleAlert,
  ChevronRight,
  Calendar as CalendarIcon,
  X as CloseIcon,
  Play,
  AlertCircle,
  Bell,
  TrendingUp,
  Activity
} from 'lucide-react';
import { Alert, AlertFeedback, FaultAccuracy, RectificationStatus } from './types/alerts.types';
import { alertsApi } from './services/alertsApi';
import { useAlerts, useAlertStats, useAlertFilters, usePMTasks } from './hooks';
import { formatDate, shiftDate, formatDateDDMonYY } from './utils';
import {
  StatsDashboard,
  AlertsTable,
  SparePartsModal,
  PMTasksOverview,
  UnifiedDashboard,
  PMTasksTable
} from './components';
import './styles/alerts-page.css';

const AlertsPortal: React.FC = () => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const { site } = useParams<{ site: string }>();
  const dateInputRef = useRef<HTMLInputElement | null>(null);

  const resolvedSiteCode = useMemo(() => {
    const fromRoute = site?.trim();
    if (fromRoute) return fromRoute;

    const fromSession = sessionStorage.getItem('tenant')?.trim();
    if (fromSession) return fromSession;

    const fromLocal = localStorage.getItem('currentSite')?.trim();
    if (fromLocal) return fromLocal;

    return undefined;
  }, [site]);

  const [headerDate, setHeaderDate] = useState<string>(() => {
    // Auto-initialize with today's date (YYYY-MM-DD format)
    return new Date().toISOString().split('T')[0];
  });

  const [savingId, setSavingId] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [editCache, setEditCache] = useState<Record<number, AlertFeedback>>({});
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [activePanel, setActivePanel] = useState<'dashboard' | 'filters' | null>(() => {
    try {
      const stored = localStorage.getItem('alerts_active_panel');
      if (stored === 'dashboard' || stored === 'filters' || stored === 'null') {
        return stored === 'null' ? null : stored as 'dashboard' | 'filters';
      }
    } catch (e) {}
    return 'dashboard';
  });

  const [activeTab, setActiveTab] = useState<'dashboard-overview' | 'pm-calendar'>(() => {
    try {
      const stored = localStorage.getItem('alerts_active_tab');
      if (stored === 'dashboard-overview' || stored === 'pm-calendar') {
        return stored as 'dashboard-overview' | 'pm-calendar';
      }
    } catch (e) {}
    return 'dashboard-overview';
  });

  useEffect(() => {
    try {
      localStorage.setItem('alerts_active_panel', activePanel === null ? 'null' : activePanel);
    } catch (e) {}
  }, [activePanel]);

  useEffect(() => {
    try {
      localStorage.setItem('alerts_active_tab', activeTab);
    } catch (e) {}
  }, [activeTab]);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalFilter, setModalFilter] = useState<{ type: string; value: string } | null>(null);
  const [runningPipeline, setRunningPipeline] = useState<boolean>(false);
  const [showSparePartsModal, setShowSparePartsModal] = useState<boolean>(false);
  const [selectedFaultCategory, setSelectedFaultCategory] = useState<string | null>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<string | undefined>(undefined);
  const [modalRefreshKey, setModalRefreshKey] = useState<number>(0);

  // ============================================================================
  // HOOKS
  // ============================================================================

  const { filters, updateFilter, resetFilters } = useAlertFilters();

  // Auto-load alerts when headerDate changes
  const { alerts, setAlerts, loading, error, totalCount, refetch } = useAlerts({
    selectedDate: headerDate,
    autoLoad: true,
    siteCode: resolvedSiteCode
  });

  // Auto-load stats on mount with site context
  const { stats, refetch: refetchStats } = useAlertStats(true, resolvedSiteCode, headerDate);

  // Auto-load PM Calendar tasks
  const { tasks: pmTasks, loading: pmTasksLoading, error: pmTasksError } = usePMTasks({
    siteCode: resolvedSiteCode,
    selectedDate: headerDate,
    autoLoad: true
  });

  // ============================================================================
  // COMPUTED VALUES - WITH FILTERS APPLIED
  // ============================================================================

  const filteredAlerts = useMemo(() => {
    let filtered = [...alerts];

    // 1. Search filter (itemname, scb_name, icr_group, description)
    if (filters.searchTerm.trim()) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(alert => {
        return (
          alert.itemname?.toLowerCase().includes(searchLower) ||
          alert.scb_name_in_field?.toLowerCase().includes(searchLower) ||
          alert.icr_group?.toLowerCase().includes(searchLower) ||
          alert.fault_description?.toLowerCase().includes(searchLower)
        );
      });
    }

    // 2. Priority filter
    if (filters.priorityFilter) {
      filtered = filtered.filter(alert =>
        alert.priority?.toLowerCase() === filters.priorityFilter.toLowerCase()
      );
    }

    // 3. Rectification status filter
    if (filters.rectificationFilter) {
      filtered = filtered.filter(alert =>
        alert.fault_rectification_status === filters.rectificationFilter
      );
    }

    // 4. Accuracy filter
    if (filters.accuracyFilter) {
      filtered = filtered.filter(alert =>
        alert.fault_accuracy === filters.accuracyFilter
      );
    }

    // 5. Sort
    filtered.sort((a, b) => {
      if (filters.sortBy === 'created_at') {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      } else if (filters.sortBy === 'deviation_percent') {
        return (b.deviation_percent || 0) - (a.deviation_percent || 0);
      } else if (filters.sortBy === 'priority') {
        const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
        return (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
      }
      return 0;
    });
return filtered;
  }, [alerts, filters]);

  // Check if no data exists for selected date
  const noDataForDate = !loading && alerts.length === 0;
  const noResultsAfterFilter = !loading && alerts.length > 0 && filteredAlerts.length === 0;

  // Modal alerts
  const modalAlerts = useMemo(() => {
    if (!showModal || !modalFilter) return [];

    return alerts.filter(alert => {
      if (modalFilter.type === 'priority') {
        return alert.priority?.toLowerCase() === modalFilter.value.toLowerCase();
      } else if (modalFilter.type === 'rectification') {
        return alert.fault_rectification_status === modalFilter.value;
      } else if (modalFilter.type === 'accuracy') {
        return alert.fault_accuracy === modalFilter.value;
      }
      return false;
    });
  }, [alerts, showModal, modalFilter]);

  // ============================================================================
  // EVENT HANDLERS - FEEDBACK FORM
  // ============================================================================

  const cancelEdit = useCallback((id: number) => {
    setExpandedRow(null);
    setEditCache(prev => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const startEdit = useCallback((alert: Alert) => {
    // Toggle: if already expanded, close it; otherwise open it
    if (expandedRow === alert.id) {
      cancelEdit(alert.id);
    } else {
      setExpandedRow(alert.id);
      setEditCache(prev => ({
        ...prev,
        [alert.id]: {
          fault_accuracy: (alert.fault_accuracy || null) as FaultAccuracy,
          fault_rectification_status: (alert.fault_rectification_status || null) as RectificationStatus,
          fault_description: alert.fault_description || '',
          no_of_faulty_strings: alert.no_of_faulty_strings || null,
          fault_category: alert.fault_category || null,
          quantum_of_fault: alert.quantum_of_fault || null,
          capacity_impacted: alert.capacity_impacted || null,
          assigned_to: alert.assigned_to_id || alert.assigned_to || null,
          assigned_at: alert.assigned_at || null,
          resolved_at: alert.resolved_at || null,
          resolved_by: alert.resolved_by_id || alert.resolved_by || null,
          energy_loss: alert.energy_loss || null,
          alert_source: alert.alert_source !== undefined ? alert.alert_source : null,
          selected_spare: alert.SELECTED_SPARE || null,
          comments: alert.comments || null,
          uploaded_image: alert.uploaded_image || null
        }
      }));
    }
  }, [expandedRow, cancelEdit]);

  const setEditField = useCallback((id: number, field: keyof AlertFeedback, value: any) => {
    setEditCache(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value }
    }));
  }, []);

  const saveEdit = useCallback(async (id: number) => {
    const edit = editCache[id];

    if (!edit || !edit.fault_description?.trim()) {
      setErrorMsg('Fault description is required');
      return;
    }

    setSavingId(id);
    const previousAlerts = [...alerts];

    setAlerts(alerts.map(a => {
      if (a.id === id) {
        return {
          ...a,
          fault_accuracy: edit.fault_accuracy as FaultAccuracy,
          fault_rectification_status: edit.fault_rectification_status as RectificationStatus,
          fault_description: edit.fault_description,
          no_of_faulty_strings: edit.no_of_faulty_strings || null,
          fault_category: edit.fault_category || null,
          quantum_of_fault: edit.quantum_of_fault || null,
          capacity_impacted: edit.capacity_impacted || null,
          assigned_to: edit.assigned_to || null,
          assigned_to_id: edit.assigned_to || null,
          assigned_at: edit.assigned_at || null,
          resolved_at: edit.resolved_at || null,
          resolved_by: edit.resolved_by || null,
          resolved_by_id: edit.resolved_by || null,
          energy_loss: edit.energy_loss || null,
          alert_source: edit.alert_source !== undefined && edit.alert_source !== null ? edit.alert_source : a.alert_source,
          comments: edit.comments || null,
          uploaded_image: edit.uploaded_image || null
        };
      }
      return a;
    }));

    try {
      // Sanitize fields to ensure lowercase values for enums
      const sanitizedEdit = {
        ...edit,
        fault_accuracy: typeof edit.fault_accuracy === 'string' ? edit.fault_accuracy.toLowerCase() as FaultAccuracy : edit.fault_accuracy,
        fault_rectification_status: typeof edit.fault_rectification_status === 'string' ? edit.fault_rectification_status.toLowerCase() as RectificationStatus : edit.fault_rectification_status,
      };

      const updated = await alertsApi.updateAlert(id, sanitizedEdit, resolvedSiteCode);
      // Merge the updated response with the edit cache to ensure all fields are properly updated
      setAlerts(curr => curr.map(a => {
        if (a.id === id) {
          // Use edit cache values (what user entered) as primary source, fallback to backend response
          return {
            ...a,
            ...updated, // Get all fields from backend response first
            // Override with edit cache values to ensure user input is preserved
            fault_accuracy: edit.fault_accuracy !== null && edit.fault_accuracy !== undefined ? edit.fault_accuracy : (updated.fault_accuracy ?? a.fault_accuracy),
            fault_rectification_status: edit.fault_rectification_status !== null && edit.fault_rectification_status !== undefined ? edit.fault_rectification_status : (updated.fault_rectification_status ?? a.fault_rectification_status),
            rectification_status: edit.fault_rectification_status !== null && edit.fault_rectification_status !== undefined ? edit.fault_rectification_status : (updated.rectification_status ?? a.rectification_status),
            fault_description: edit.fault_description || updated.fault_description || a.fault_description,
            no_of_faulty_strings: edit.no_of_faulty_strings !== null && edit.no_of_faulty_strings !== undefined ? edit.no_of_faulty_strings : (updated.no_of_faulty_strings ?? a.no_of_faulty_strings),
            fault_category: edit.fault_category || updated.fault_category || a.fault_category,
            quantum_of_fault: edit.quantum_of_fault !== null && edit.quantum_of_fault !== undefined ? edit.quantum_of_fault : (updated.quantum_of_fault ?? a.quantum_of_fault),
            capacity_impacted: edit.capacity_impacted !== null && edit.capacity_impacted !== undefined ? edit.capacity_impacted : (updated.capacity_impacted ?? a.capacity_impacted),
            energy_loss: edit.energy_loss !== null && edit.energy_loss !== undefined ? edit.energy_loss : (updated.energy_loss ?? a.energy_loss),
            assigned_to: edit.assigned_to !== null && edit.assigned_to !== undefined ? edit.assigned_to : (updated.assigned_to ?? a.assigned_to),
            assigned_to_id: edit.assigned_to !== null && edit.assigned_to !== undefined ? edit.assigned_to : (updated.assigned_to_id ?? a.assigned_to_id),
            assigned_at: edit.assigned_at || updated.assigned_at || a.assigned_at,
            resolved_at: edit.resolved_at || updated.resolved_at || a.resolved_at,
            resolved_by: edit.resolved_by !== null && edit.resolved_by !== undefined ? edit.resolved_by : (updated.resolved_by ?? a.resolved_by),
            resolved_by_id: edit.resolved_by !== null && edit.resolved_by !== undefined ? edit.resolved_by : (updated.resolved_by_id ?? a.resolved_by_id),
            alert_source: edit.alert_source !== undefined && edit.alert_source !== null ? edit.alert_source : (updated.alert_source ?? a.alert_source),
            comments: edit.comments !== null && edit.comments !== undefined ? edit.comments : (updated.comments ?? a.comments),
          };
        }
        return a;
      }));

      // Check for warnings in the response (e.g., invalid user IDs)
      if ((updated as any).warnings) {
        const warnings = (updated as any).warnings;
        const warningMessages = Object.values(warnings).join('; ');
        setOkMsg(`Feedback saved successfully! Warning: ${warningMessages}`);
      } else {
        setOkMsg('Feedback saved successfully!');
      }

      // Refresh alerts to get the latest data from backend
      await refetch();
      await refetchStats();
      cancelEdit(id);
    } catch (err: any) {
      setAlerts(previousAlerts);
      setErrorMsg(`Save failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setSavingId(null);
    }
  }, [editCache, alerts, setAlerts, refetch, refetchStats, cancelEdit, resolvedSiteCode]);

  // ============================================================================
  // EVENT HANDLERS - PIPELINE & DATA
  // ============================================================================

  const runPipeline = useCallback(async () => {
    if (!headerDate) {
      setErrorMsg('No date selected');
      return;
    }

    setRunningPipeline(true);
    setErrorMsg(null);
    setOkMsg('Running pipeline... This may take a few moments');

    try {
      const result = await alertsApi.generateAlerts(headerDate, resolvedSiteCode);

      if (result.success) {
        setOkMsg(`Pipeline completed successfully! ${result.message || ''}`);
        setTimeout(async () => {
          await Promise.all([refetch(), refetchStats()]);
          setOkMsg('Data refreshed successfully!');
        }, 2000);
      } else {
        setErrorMsg(`Pipeline failed: ${result.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      setErrorMsg(`Pipeline error: ${err?.message || 'Network error occurred'}`);
    } finally {
      setRunningPipeline(false);
    }
  }, [headerDate, refetch, refetchStats, resolvedSiteCode]);

  const handleDateChange = useCallback((newDate: string) => {
setHeaderDate(newDate);
    updateFilter('selectedDate', newDate);
    resetFilters(); // Reset filters when date changes
  }, [updateFilter, resetFilters]);

  const handleDateShift = useCallback((days: number) => {
    const newDate = shiftDate(headerDate, days);
    handleDateChange(newDate);
  }, [headerDate, handleDateChange]);

  const openDatePicker = useCallback(() => {
    const input = dateInputRef.current;
    if (!input) return;
    // Prefer showPicker when available (Chromium), fallback to click/focus
    if (typeof (input as any).showPicker === 'function') {
      (input as any).showPicker();
    } else {
      input.focus();
      input.click();
    }
  }, []);

  const handleRefresh = useCallback(() => {
refetch();
  }, [refetch]);

  const togglePanel = useCallback((panel: 'dashboard' | 'filters') => {
    setActivePanel(prev => prev === panel ? null : panel);
  }, []);

  // ============================================================================
  // EVENT HANDLERS - MODAL
  // ============================================================================

  const handleStatClick = useCallback((filterType: string, filterValue: string) => {
    setModalFilter({ type: filterType, value: filterValue });
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setModalFilter(null);
  }, []);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    if (okMsg || errorMsg) {
      const timer = setTimeout(() => {
        setOkMsg(null);
        setErrorMsg(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [okMsg, errorMsg]);

  useEffect(() => {
    if (error) setErrorMsg(`${error}`);
  }, [error]);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const getModalTitle = () => {
    if (!modalFilter) return '';

    if (modalFilter.type === 'priority') {
      return `${modalFilter.value.charAt(0).toUpperCase() + modalFilter.value.slice(1)} Priority Alerts`;
    } else if (modalFilter.type === 'rectification') {
      return `${modalFilter.value === 'rectified' ? 'Rectified' : 'Not Rectified'} Alerts`;
    } else if (modalFilter.type === 'accuracy') {
      return `${modalFilter.value === 'true' ? 'Accurate' : 'Inaccurate'} Alerts`;
    }
    return '';
  };

  const isToday = headerDate >= new Date().toISOString().split('T')[0];

  // Check if any filters are active
  const hasActiveFilters = filters.searchTerm || filters.priorityFilter ||
    filters.rectificationFilter || filters.accuracyFilter;

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
        .alerts-page,
        .alerts-page *,
        .alerts-page *::before,
        .alerts-page *::after,
        .alerts-page *::placeholder {
          font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
          font-weight: 400 !important;
        }
        .alerts-page input,
        .alerts-page input::placeholder,
        .alerts-page textarea,
        .alerts-page textarea::placeholder,
        .alerts-page select,
        .alerts-page select option,
        .alerts-page label,
        .alerts-page button,
        .alerts-page span,
        .alerts-page div,
        .alerts-page p,
        .alerts-page td,
        .alerts-page th,
        .alerts-page h1,
        .alerts-page h2,
        .alerts-page h3,
        .alerts-page h4,
        .alerts-page h5,
        .alerts-page h6 {
          font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
          font-weight: 400 !important;
        }
        /* Only allow bold for specific elements that should be bold */
        .alerts-page h1,
        .alerts-page h2,
        .alerts-page h3,
        .alerts-page h4,
        .alerts-page h5,
        .alerts-page h6,
        .alerts-page strong,
        .alerts-page b,
        .alerts-page .font-bold,
        .alerts-page .font-semibold {
          font-weight: 600 !important;
        }
      `}} />
      <div className="alerts-page" style={{ fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }}>

        {/* Header */}
        <div className="alerts-header">
          <h2 className="alerts-page-title">
            <div className="inline-flex p-2 bg-[#0084CF] rounded-lg">
              <TriangleAlert size={19} className="title-icon text-white" />
            </div>
            Alerts Portal
          </h2>

          <div className="header-controls">
            <div className="date-filter-group">
              <button
                onClick={() => handleDateShift(-1)}
                className="date-nav-btn"
                title="Previous day" aria-label="Previous day"
              >
                <ChevronLeft size={18} />
              </button>
              <div className="date-picker-wrapper">
                <input
                  ref={dateInputRef}
                  type="date"
                  value={headerDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  className="date-picker-input"
                  aria-label="Select date"
                />
                <button
                  type="button"
                  className="date-picker-display"
                  onClick={openDatePicker}
                  title="Click to select date" aria-label="Click to select date"
                >
                  {formatDateDDMonYY(headerDate)}
                </button>
              </div>
              <button
                onClick={() => handleDateShift(1)}
                disabled={isToday}
                className="date-nav-btn"
                title="Next day" aria-label="Next day"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="alerts-actions">
              <button
                onClick={runPipeline}
                className={`btn btn-pipeline ${noDataForDate ? 'btn-pipeline-highlight' : ''}`}
                disabled={runningPipeline}
                title={noDataForDate ? 'No data found - Click to generate alerts' : 'Generate alerts for selected date'}
              >
                {runningPipeline ? (
                  <RefreshCw size={18} className="spinner" />
                ) : (
                  <Play size={18} />
                )}
                {noDataForDate ? 'Run Pipeline (No Data)' : 'Run Pipeline'}
              </button>
              <button
                onClick={handleRefresh}
                className="btn btn-refresh"
                disabled={loading}
                title="Refresh data" aria-label="Refresh data"
              >
                <RefreshCw size={18} className={loading ? 'spinner' : ''} />
                Refresh
              </button>
              <button
                onClick={() => togglePanel('filters')}
                className={`btn ${activePanel === 'filters' ? 'btn-active' : ''} ${hasActiveFilters ? 'btn-filters-active' : ''}`}
                title="Toggle filters" aria-label="Toggle filters"
              >
                <Filter size={18} />
                Filters
                {hasActiveFilters && <span className="filter-badge">{
                  [filters.searchTerm, filters.priorityFilter, filters.rectificationFilter, filters.accuracyFilter]
                    .filter(Boolean).length
                }</span>}
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation Row */}
        <div className="alerts-tab-navigation">
          <button
            className={`alerts-tab-btn ${activeTab === 'dashboard-overview' ? 'alerts-tab-active' : ''}`}
            onClick={() => setActiveTab('dashboard-overview')}
          >
            <TrendingUp size={16} />
            <span>Dashboard Overview</span>
          </button>
          <button
            className={`alerts-tab-btn ${activeTab === 'pm-calendar' ? 'alerts-tab-active' : ''}`}
            onClick={() => setActiveTab('pm-calendar')}
          >
            <Activity size={16} />
            <span>PM Calendar Activities</span>
          </button>
        </div>

        {/* Filters Panel */}
        {activePanel === 'filters' && (
          <div className="panel-slide panel-filters">
            <UnifiedDashboard 
              activeTab={activeTab}
              stats={stats} 
              pmTasks={pmTasks}
              pmTasksLoading={pmTasksLoading}
              onStatClick={handleStatClick}
            />
          </div>
        )}

        {/* PM Calendar Activities Tab Content */}
        {activeTab === 'pm-calendar' && (
          <div className="panel-slide panel-dashboard">
            <div className="dashboard-content">
              <div className="dashboard-tables-section">
                <PMTasksTable 
                  tasks={pmTasks} 
                  loading={pmTasksLoading} 
                  error={pmTasksError}
                />
              </div>
            </div>
          </div>
        )}

        {/* Messages */}
        {(errorMsg || okMsg) && (
          <div className="alerts-messages">
            {errorMsg && (
              <div className="alert alert-error">
                <XCircle size={18} />
                <span>{errorMsg}</span>
              </div>
            )}
            {okMsg && (
              <div className="alert alert-success">
                <CheckCircle2 size={18} />
                <span>{okMsg}</span>
              </div>
            )}
          </div>
        )}

        {/* No Data Warning (Dashboard Overview only) */}
        {activeTab === 'dashboard-overview' && noDataForDate && (
          <div className="no-data-banner">
            <AlertCircle size={20} />
            <div className="no-data-content">
              <strong>No alerts found for {formatDate(headerDate)}</strong>
              <p>Click "Run Pipeline" to generate alerts for this date</p>
            </div>
            <button
              onClick={runPipeline}
              className="btn btn-pipeline btn-pipeline-cta"
              disabled={runningPipeline}
            >
              <Play size={14} />
              Run Pipeline Now
            </button>
          </div>
        )}

        {/* No Results After Filter (Dashboard Overview only) */}
        {activeTab === 'dashboard-overview' && noResultsAfterFilter && (
          <div className="no-results-banner">
            <AlertCircle size={20} />
            <div className="no-data-content">
              <strong>No alerts match your filters</strong>
              <p>Try adjusting or clearing the filters</p>
            </div>
            <button
              onClick={resetFilters}
              className="btn btn-secondary"
            >
              <XCircle size={18} />
              Clear Filters
            </button>
          </div>
        )}

        {/* Info Bar + Main Alerts Table (Dashboard Overview only) */}
        {activeTab === 'dashboard-overview' && (
          <>
            <div className="info-bar">
              <div className="info-item">
                <CalendarIcon size={16} />
                <span>Showing alerts for: <strong>{formatDate(headerDate)}</strong></span>
              </div>
              <div className="info-item">
                <div className="bell-icon-container">
                  <Bell size={40} />
                  <span className="bell-badge">{filteredAlerts.length}</span>
                </div>
                {hasActiveFilters && <span className="filtered-text"> filtered</span>}
                {hasActiveFilters && alerts.length > 0 && (
                  <span className="total-count"> (of {alerts.length} total)</span>
                )}
              </div>
            </div>

            {/* Main Table */}
            <div className="alerts-table-wrapper">
              <AlertsTable
                alerts={filteredAlerts}
                loading={loading}
                expandedRow={expandedRow}
                editCache={editCache}
                savingId={savingId}
                selectedDate={headerDate}
                onStartEdit={startEdit}
                onCancelEdit={cancelEdit}
                onSaveEdit={saveEdit}
                onSetEditField={setEditField}
                onFaultCategorySelect={(faultCategory) => {
                  // Get the equipment from the expanded alert
                  const alert = alerts.find(a => a.id === expandedRow);
                  const equipment = alert ? (alert.EQUIPMENT || alert.itemname) : undefined;

                  // Always update the fault category and refresh key to force modal refresh
                  setSelectedFaultCategory(faultCategory);
                  setSelectedEquipment(equipment);
                  setModalRefreshKey(prev => prev + 1); // Increment key to force refresh
                  setShowSparePartsModal(true);
                }}
              />
            </div>
          </>
        )}

        {/* Modal */}
        {showModal && modalFilter && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">
                  {getModalTitle()}
                  <span>(All Dates)</span>
                </h3>
                <button className="modal-close" onClick={closeModal}>
                  <CloseIcon size={20} />
                </button>
              </div>
              <div className="modal-body">
                <p className="modal-count">
                  {modalAlerts.length} alert{modalAlerts.length !== 1 ? 's' : ''} found across all dates
                </p>
                <div className="modal-table-wrapper">
                  <AlertsTable
                    alerts={modalAlerts}
                    loading={false}
                    expandedRow={expandedRow}
                    editCache={editCache}
                    savingId={savingId}
                    selectedDate={headerDate}
                    onStartEdit={startEdit}
                    onCancelEdit={cancelEdit}
                    onSaveEdit={saveEdit}
                    onSetEditField={setEditField}
                    onFaultCategorySelect={(faultCategory) => {
                      // Get the equipment from the expanded alert in modalAlerts
                      const alert = modalAlerts.find(a => a.id === expandedRow);
                      const equipment = alert ? (alert.EQUIPMENT || alert.itemname) : undefined;

                      // Always update the fault category and refresh key to force modal refresh
                      setSelectedFaultCategory(faultCategory);
                      setSelectedEquipment(equipment);
                      setModalRefreshKey(prev => prev + 1); // Increment key to force refresh
                      setShowSparePartsModal(true);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Spare Parts Modal */}
        <SparePartsModal
          key={modalRefreshKey} // Force remount when key changes
          isOpen={showSparePartsModal}
          onClose={() => {
            setShowSparePartsModal(false);
            setSelectedFaultCategory(null);
            setSelectedEquipment(undefined);
          }}
          faultCategory={selectedFaultCategory}
          equipmentName={selectedEquipment}
          siteCode={resolvedSiteCode}
          onSpareSelect={(spareDescription: string, spareId?: number, quantity?: number, actionType?: string) => {
            // Update the selected_spare field in the edit cache for the expanded row
            if (expandedRow !== null) {
              setEditField(expandedRow, 'selected_spare', spareDescription);

              if (spareId) setEditField(expandedRow, 'spare_part_id', spareId);
              if (quantity) setEditField(expandedRow, 'spare_quantity', quantity);
              if (actionType) setEditField(expandedRow, 'spare_action_type', actionType);
            }
          }}
        />
      </div>
    </>
  );
};

export default AlertsPortal;

